//************************************************************************ /
// CARD DETAILS
// Displays the title, host, times, and location (optional) for Event
// and Campaign Cards.
//************************************************************************ /

import React, { useEffect } from "react";
import { getFormattedEventTimes } from "@dreambigger/shared/src/utils";
import {
  Typography,
  Group,
} from "@dreambigger/design-system/src/components/atoms";

const { Title, Text } = Typography;

export interface CardDetailProps {
  allDay: boolean;
  title?: string;
  host?: string;
  location?: string;
  startAt?: string;
  endAt?: string;
  font?: string;
  setBannerEnabled?: Function;
  timeZone: string | null;
}

const CardDetails = ({
  allDay,
  title,
  host,
  location,
  startAt,
  endAt,
  font,
  setBannerEnabled,
  timeZone,
}: CardDetailProps) => {
  // Guard clause against lack of necessary data
  if (!startAt || !endAt || !timeZone) {
    console.warn(
      "Card details could not be rendered because a startAt, endAt, or timeZone was not provided"
    );
    return <></>;
  }

  // Get event times for description. Return if there is an error.
  const eventTimes = getFormattedEventTimes(startAt, endAt, timeZone);
  if (!eventTimes) {
    console.warn("Event times were not returned correctly");
    return <></>;
  }

  // Enable "Happening Now" banner if applicable
  useEffect(() => {
    setBannerEnabled && setBannerEnabled(eventTimes.happeningNow);
  }, [eventTimes]);

  return (
    <>
      <Group>
        {/* TITLE */}
        <Title level={4} className="lh-3">
          {title}
        </Title>
        {/* HOST */}
        {host && <Text className="gray-8 f-2">Hosted by: {host}</Text>}
      </Group>
      <Group className="mt-4">
        {/* DATE */}
        <Text className="fwb" style={{ fontFamily: font }}>
          {eventTimes.dates}
        </Text>
        {/* TIME */}
        <Text style={{ fontFamily: font }}>
          {/* If allDay is flagged, then display time as "All Day" */}
          {allDay
            ? "All Day"
            : `${eventTimes.times} ${eventTimes.abbrTimezone}`}
        </Text>
        {/* LOCATION */}
        {location && (
          <Text className="gray-8 f-2 mt-2" style={{ fontFamily: font }}>
            {location}
          </Text>
        )}
      </Group>
    </>
  );
};

export default CardDetails;
